
import { Component, Vue } from 'vue-property-decorator'
import { MessageBox } from 'element-ui'

import { payMultiChoiceCardOrder, rescheduleMultiChoiceCardOrder } from '@/api/multiChoiceCardOrders'
import { MultiChoiceCardOrder, BankTransfer, Budget, BudgetMove } from '@/models'
import { extractApiErrorMessages } from '@/utils/apiErrors'
import OrderListCard from './components/OrderListCard.vue'
import AcompteListCard from './components/AcompteListCard.vue'
import { DotationOrderListItem } from './types/dotationOrderList'
import { AcompteListItem } from './types/acompteList'
import { mapOrderToListItem } from './utils/mapOrder'
import { mapAcompteToListItem } from './utils/mapAcompte'

type ListItem =
  | { type: 'order'; order: DotationOrderListItem; createdAt: string }
  | { type: 'acompte'; acompte: AcompteListItem; createdAt: string }

@Component({
  name: 'DotationOrdersList',
  components: {
    OrderListCard,
    AcompteListCard
  }
})
export default class extends Vue {
  orders: DotationOrderListItem[] = []
  acomptes: AcompteListItem[] = []
  budget: Budget | null = null
  reservedOrderSummaries: { reference: string; amount: number }[] = []
  expandedOrderId: string | null = null
  loading = false
  payingOrderId: string | null = null
  reschedulingOrderId: string | null = null

  showOrders = true
  showAcomptes = true

  created(): void {
    this.loadItems()
  }

  activated(): void {
    this.loadItems()
  }

  get items(): ListItem[] {
    const orderItems: ListItem[] = this.orders.map(order => ({ type: 'order', order, createdAt: order.createdAt }))
    const acompteItems: ListItem[] = this.acomptes.map(acompte => ({ type: 'acompte', acompte, createdAt: acompte.createdAt }))

    return [...orderItems, ...acompteItems].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }

  get visibleItems(): ListItem[] {
    return this.items.filter(item => (item.type === 'order' ? this.showOrders : this.showAcomptes))
  }

  get availableBudget(): number {
    if (!this.budget) return 0
    return this.budget.amount - (this.budget.reservedAmount || 0)
  }

  get reservedOrderHintItems(): string[] {
    return this.reservedOrderSummaries.map(item => `${item.reference} (${this.$n(item.amount, 'currency')})`)
  }

  reservedAmountFor(order: DotationOrderListItem): number {
    return this.reservedOrderSummaries.find(item => item.reference === order.reference)?.amount || 0
  }

  async loadItems(): Promise<void> {
    this.loading = true
    try {
      const [{ data: orders }, { data: acomptes }, { data: budget }, { data: reservedMoves }] = await Promise.all([
        MultiChoiceCardOrder
          .includes(['multiChoiceGiftCard', { multiChoiceCardOrderLines: ['customer'] }])
          .order({ createdAt: 'desc' })
          .page(1)
          .per(50)
          .all(),
        BankTransfer
          .order({ createdAt: 'desc' })
          .page(1)
          .per(50)
          .all(),
        Budget.first(),
        BudgetMove
          .where({ reserved: true, creditedAt: { '!eq': null } })
          .includes(['multiChoiceCardOrder'])
          .page(1)
          .per(50)
          .all()
      ])

      this.orders = orders.map(mapOrderToListItem)
      this.acomptes = acomptes.map(mapAcompteToListItem)
      this.budget = budget
      const amountByReference = new Map<string, number>()
      reservedMoves.forEach(move => {
        const reference = move.multiChoiceCardOrder?.number
        if (!reference) return

        amountByReference.set(reference, (amountByReference.get(reference) || 0) + move.amount)
      })
      this.reservedOrderSummaries = Array.from(amountByReference, ([reference, amount]) => ({ reference, amount }))
      if (!this.expandedOrderId || !this.orders.some(order => order.id === this.expandedOrderId)) {
        this.expandedOrderId = this.orders[0]?.id || null
      }
    } catch (err: any) {
      const messages = extractApiErrorMessages(err)
      this.$notify({
        title: String(this.$t('dotationOrders.notifications.loadErrorTitle')),
        message: messages.join('\n') || String(this.$t('dotationOrders.notifications.loadError')),
        type: 'error',
        duration: 6000
      })
      this.orders = []
      this.acomptes = []
      this.reservedOrderSummaries = []
    } finally {
      this.loading = false
    }
  }

  goToNewOrder(): void {
    this.$router.push('/gifts/new-order')
  }

  goToNewAcompte(): void {
    this.$router.push('/gifts/new-acompte')
  }

  toggleOrder(orderId: string): void {
    this.expandedOrderId = this.expandedOrderId === orderId ? null : orderId
  }

  async payOrder(order: DotationOrderListItem): Promise<void> {
    this.payingOrderId = order.id
    try {
      await payMultiChoiceCardOrder(order.id)
      this.$message.success(this.$t('dotationOrders.notifications.paid') as string)
      await this.loadItems()
    } catch (err: any) {
      const messages = extractApiErrorMessages(err)
      this.$notify({
        title: String(this.$t('dotationOrders.notifications.payErrorTitle')),
        message: messages.join('\n') || String(this.$t('dotationOrders.notifications.payError')),
        type: 'error',
        duration: 8000
      })
    } finally {
      this.payingOrderId = null
    }
  }

  async rescheduleAndPay(order: DotationOrderListItem, scheduledAt: string | null): Promise<void> {
    this.reschedulingOrderId = order.id
    try {
      await rescheduleMultiChoiceCardOrder(order.id, scheduledAt)
      await this.loadItems()
    } catch (err: any) {
      const messages = extractApiErrorMessages(err)
      this.$notify({
        title: String(this.$t('dotationOrders.schedule.rescheduleErrorTitle')),
        message: messages.join('\n') || String(this.$t('dotationOrders.schedule.rescheduleError')),
        type: 'error',
        duration: 8000
      })
      return
    } finally {
      this.reschedulingOrderId = null
    }

    await this.payOrder(order)
  }

  async deleteOrder(orderId: string): Promise<void> {
    try {
      await MessageBox.confirm(
        this.$t('dotationOrders.delete.confirmMessage') as string,
        this.$t('dotationOrders.delete.confirmTitle') as string,
        {
          confirmButtonText: this.$t('dotationOrders.actions.confirm') as string,
          cancelButtonText: this.$t('dotationOrders.actions.cancel') as string,
          type: 'warning'
        }
      )
    } catch {
      return
    }

    try {
      const { data: order } = await MultiChoiceCardOrder.find(orderId)
      await order.destroy()
      this.$message.success(this.$t('dotationOrders.notifications.deleted') as string)
      await this.loadItems()
    } catch (err: any) {
      const messages = extractApiErrorMessages(err)
      this.$notify({
        title: String(this.$t('dotationOrders.notifications.deleteErrorTitle')),
        message: messages.join('\n') || String(this.$t('dotationOrders.notifications.deleteError')),
        type: 'error',
        duration: 6000
      })
    }
  }

  async deleteAcompte(acompteId: string): Promise<void> {
    try {
      await MessageBox.confirm(
        this.$t('dotationOrders.acompte.delete.confirmMessage') as string,
        this.$t('dotationOrders.acompte.delete.confirmTitle') as string,
        {
          confirmButtonText: this.$t('dotationOrders.actions.confirm') as string,
          cancelButtonText: this.$t('dotationOrders.actions.cancel') as string,
          type: 'warning'
        }
      )
    } catch {
      return
    }

    try {
      const { data: acompte } = await BankTransfer.find(acompteId)
      await acompte.destroy()
      this.$message.success(this.$t('dotationOrders.notifications.acompteDeleted') as string)
      await this.loadItems()
    } catch (err: any) {
      const messages = extractApiErrorMessages(err)
      this.$notify({
        title: String(this.$t('dotationOrders.notifications.acompteDeleteErrorTitle')),
        message: messages.join('\n') || String(this.$t('dotationOrders.notifications.acompteDeleteError')),
        type: 'error',
        duration: 6000
      })
    }
  }
}
